// Contrail V3.9.0 — the signed-in user's own preferences (table user_prefs, read under RLS, written by RPC):
//   hidden_devices      — devices toggled off on the map (tap the icon in the Objects list)
//   alerts_snooze_until — alert notifications snoozed until then (events are still recorded)
//   alerts_muted        — notifications off until turned back on
// One shared copy per page; components subscribe with useUserPrefs().
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export type Prefs = { hidden_devices: string[]; alerts_snooze_until: string | null; alerts_muted: boolean };
const EMPTY: Prefs = { hidden_devices: [], alerts_snooze_until: null, alerts_muted: false };
let current: Prefs = EMPTY;
let loaded = false;
let loading: Promise<void> | null = null;
const subs = new Set<(p: Prefs) => void>();
const emit = () => subs.forEach((f) => f(current));

async function load() {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return;
  const { data } = await supabase.from("user_prefs").select("hidden_devices, alerts_snooze_until, alerts_muted").eq("user_id", u.user.id).maybeSingle();
  current = { ...EMPTY, ...(data ?? {}), hidden_devices: (data?.hidden_devices as string[] | null) ?? [] };
  loaded = true;
  emit();
}

export function useUserPrefs() {
  const [p, setP] = useState<Prefs>(current);
  useEffect(() => {
    subs.add(setP);
    if (!loaded && !loading) loading = load().finally(() => { loading = null; });
    else setP(current);
    return () => { subs.delete(setP); };
  }, []);
  return p;
}

/** Show / hide one device on the map (optimistic; rolls back if saving fails). */
export async function toggleHidden(id: string) {
  const before = current;
  const set = new Set(current.hidden_devices);
  set.has(id) ? set.delete(id) : set.add(id);
  current = { ...current, hidden_devices: [...set] };
  emit();
  const { error } = await supabase.rpc("set_hidden_devices", { p_ids: current.hidden_devices });
  if (error) { current = before; emit(); throw new Error(error.message); }
}

export async function setSnooze(until: Date | null, muted = false) {
  const before = current;
  current = { ...current, alerts_snooze_until: until ? until.toISOString() : null, alerts_muted: muted };
  emit();
  const { error } = await supabase.rpc("set_alert_snooze", { p_until: until ? until.toISOString() : null, p_muted: muted });
  if (error) { current = before; emit(); throw new Error(error.message); }
}

/** Is the user snoozed / muted right now? */
export function quietNow(p: Prefs, now = Date.now()) {
  if (p.alerts_muted) return { quiet: true as const, label: "Notifications off" };
  if (p.alerts_snooze_until && Date.parse(p.alerts_snooze_until) > now) {
    const t = new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(p.alerts_snooze_until));
    return { quiet: true as const, label: `Snoozed until ${t}` };
  }
  return { quiet: false as const, label: "" };
}

/** "Till tomorrow" = 07:00 Fiji time tomorrow. */
export function tomorrowMorning(now = new Date()) {
  const fiji = new Date(now.getTime() + 12 * 3600_000);          // Fiji is UTC+12 (no daylight saving at present)
  const d = new Date(Date.UTC(fiji.getUTCFullYear(), fiji.getUTCMonth(), fiji.getUTCDate() + 1, 7, 0, 0));
  return new Date(d.getTime() - 12 * 3600_000);
}
